import type { ExperimentId } from "../../global";
import { emptyPaginatedData } from "./helper";
import api, { queryClient } from "@app/config/api/api";
import type { operations } from "@generated/openapi";
import { notifications } from "@lib/notification/notification";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";

export type ListExperimentsQuery = operations["list_experiments"]["parameters"]["query"];
export type ListExperimentDatapointsQuery = operations["list_experiment_datapoints"]["parameters"]["query"];

export const useExperimentList = (query: ListExperimentsQuery = {}) =>
  api.useSuspenseQuery("get", "/experiments/", { params: { query } }, { initialData: emptyPaginatedData });

export const useExperimentListWithSorting = (query: ListExperimentsQuery) =>
  api.useSuspenseQuery("get", "/experiments/", { params: { query } });

export const experimentDetailsOptions = (experiment_id: ExperimentId) =>
  api.queryOptions("get", `/experiments/{experiment_id}`, {
    params: { path: { experiment_id } },
    queryKey: ["experiments", experiment_id]
  });

export const useExperimentDetailsSuspense = (experiment_id: ExperimentId) =>
  useSuspenseQuery(experimentDetailsOptions(experiment_id));

export const useExperimentDetails = (experiment_id?: ExperimentId) =>
  useQuery({
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ...experimentDetailsOptions(experiment_id!),
    enabled: Boolean(experiment_id)
  });

export const usePostExperiment = () =>
  api.useMutation("post", "/experiments/", {
    onSuccess: () => notifications.success("Experiment created successfully")
  });

export const useDeleteExperiment = () =>
  api.useMutation("delete", "/experiments/{experiment_id}", {
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: api.queryOptions("get", "/experiments/").queryKey });
      notifications.success("Experiment deleted.");
    },
    onError(error) {
      notifications.error("Failed to delete experiment", error.message);
    }
  });

export const usePatchExperiment = () =>
  api.useMutation("patch", "/experiments/{experiment_id}", {
    onSuccess: async experiment => {
      await queryClient.invalidateQueries({
        queryKey: api.queryOptions("get", "/experiments/{experiment_id}", {
          params: { path: { experiment_id: experiment.id } }
        }).queryKey
      });
      notifications.success("Experiment updated.");
    }
  });

export const usePostExperimentMeasurements = () => api.useMutation("post", "/experiments/{experiment_id}:measurements");

export const useStartExperiment = () =>
  api.useMutation("post", "/experiments/{experiment_id}:start", {
    onSuccess: () => notifications.success("Experiment started"),
    onError: () => notifications.error("Failed to start experiment")
  });

export const useRegenerateExperimentRecommendations = () =>
  api.useMutation("post", "/experiments/{experiment_id}:regenerate_recommendations", {
    onError: () => notifications.error("Failed to regenerate experiment recommendations")
  });

export const useExperimentPredict = () => api.useMutation("post", "/experiments/{experiment_id}:predict");

export const invalidateExperimentList = async () => {
  return queryClient.invalidateQueries({ queryKey: api.queryOptions("get", "/experiments/").queryKey });
};

/**
 * Invalidate experiment details query.
 */
export const invalidateExperimentDetails = async (id: ExperimentId) => {
  return queryClient.invalidateQueries({ queryKey: experimentDetailsOptions(id).queryKey });
};

export const experimentModelStatusOptions = (experiment_id: ExperimentId) =>
  api.queryOptions("get", "/experiments/{experiment_id}/model-status", {
    params: { path: { experiment_id } },
    queryKey: ["experiments", experiment_id, "model-status"]
  });

export const useExperimentModelStatus = (experiment_id: ExperimentId) =>
  useQuery(experimentModelStatusOptions(experiment_id));

/**
 * Invalidate experiment model status query.
 */
export const invalidateExperimentModelStatus = async (id: ExperimentId) => {
  return queryClient.invalidateQueries({ queryKey: experimentModelStatusOptions(id).queryKey });
};

export const useRetryExperiment = () =>
  api.useMutation("post", "/experiments/{experiment_id}:retry", {
    onSuccess: () => notifications.success("Experiment retry initiated"),
    onError: () => notifications.error("Failed to retry experiment")
  });

export const useRetrainExperiment = () =>
  api.useMutation("post", "/experiments/{experiment_id}:retrain", {
    onSuccess: () => notifications.success("Model retraining initiated"),
    onError: () => notifications.error("Failed to retrain model")
  });

export const experimentRecommendationsOptions = (experiment_id: ExperimentId) =>
  api.queryOptions("get", "/experiments/{experiment_id}/recommendations", {
    params: { path: { experiment_id } }
  });

export const useExperimentRecommendations = (experiment_id: ExperimentId) =>
  api.useQuery("get", "/experiments/{experiment_id}/recommendations", {
    params: { path: { experiment_id } }
  });

export const invalidateExperimentRecommendations = async (id: ExperimentId) => {
  return queryClient.invalidateQueries({ queryKey: experimentRecommendationsOptions(id).queryKey });
};

export const useExperimentDatapointsList = (query: ListExperimentDatapointsQuery, experiment_id: ExperimentId) =>
  api.useQuery("get", "/experiments/{experiment_id}/datapoints/", {
    params: { query, path: { experiment_id } }
  });

export const useCreateUserInsightRating = (experimentId: ExperimentId) =>
  api.useMutation("post", "/experiments/runs/{run_id}:rate", {
    onSuccess: async () => {
      await invalidateExperimentRecommendations(experimentId);
      notifications.success("Rating submitted successfully");
    },
    onError: () => notifications.error("Failed to submit rating")
  });

export const useCreateExperimentDraft = () =>
  api.useMutation("post", "/experiments/drafts", {
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: api.queryOptions("get", "/experiments/").queryKey });
    }
  });

export const usePublishExperimentDraft = () =>
  api.useMutation("post", "/experiments/{experiment_id}:publish", {
    onSuccess: () => notifications.success("Experiment published")
  });

export const usePatchExperimentDraft = () => api.useMutation("patch", "/experiments/{experiment_id}");

export const useComputeOptimizationDifficulty = () =>
  api.useMutation("post", "/experiments/compute-optimization-difficulty");

export type ExperimentExportFormat = "md" | "json";

export const exportExperimentToFile = async (experiment_id: ExperimentId, format: ExperimentExportFormat) =>
  queryClient.fetchQuery(
    api.queryOptions(
      "get",
      `/experiments/{experiment_id}:export`,
      {
        params: { path: { experiment_id }, query: { format } },
        parseAs: "blob"
      },
      {
        enabled: false
      }
    )
  );
